import { isEqual } from 'lodash-es';
import type { ForwardedRef } from 'react';
import { forwardRef, useEffect, useRef, useState } from 'react';

import { useBaseInput } from '../VBaseInput';
import { VChip } from '../VChip';
import { VFlex } from '../VFlex';
import type { VInnerChipInputProps } from './type';

const VInnerChipInput = forwardRef(
  (props: VInnerChipInputProps, ref: ForwardedRef<HTMLDivElement>) => {
    const { value, valueValidator, onChange, autoFocus } = props;
    const { id, status, setStatus } = useBaseInput();
    const disabled = status.disabled;
    const [innerValue, setInnerValue] = useState(value);
    const [inputValue, setInputValue] = useState('');

    const inputRef = useRef<HTMLInputElement>(null);

    const isChanged = () => isEqual(value.toSorted(), innerValue.toSorted());

    const addChip = (item: string) => {
      setInnerValue([...innerValue, item]);
    };

    const removeChip = (index: number) => {
      if (index < 0 || index >= innerValue.length) return;

      const nextInnerItemList = innerValue.filter((_, i) => i !== index);

      setInnerValue(nextInnerItemList);
      inputRef.current?.focus();
    };

    const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = e => {
      const targetKeys = ['Enter', ',', ' '];
      if (targetKeys.includes(e.key)) {
        e.preventDefault();
        addValue();
      } else if (e.key === 'Backspace' && !inputValue) {
        e.preventDefault();
        removeChip(innerValue.length - 1);
      }
    };

    const addValue = () => {
      if (!inputValue.length) return;

      addChip(inputValue);
      setInputValue('');
      inputRef.current?.focus();
    };

    useEffect(() => {
      if (isChanged()) return;

      onChange?.(innerValue);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [innerValue]);

    useEffect(() => {
      if (isChanged()) return;

      setInnerValue(value);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    return (
      <VFlex
        ref={ref}
        items={'center'}
        justify={'start'}
        gap={0.25}
        wrap="wrap"
        className="w-full"
      >
        {innerValue.map((item, index) => {
          const isValid =
            valueValidator instanceof RegExp
              ? valueValidator.test(item)
              : (valueValidator?.(item) ?? true);

          return (
            <VChip
              key={index}
              disabled={disabled ?? false}
              onDelete={() => removeChip(index)}
              valid={isValid}
            >
              {item}
            </VChip>
          );
        })}

        {!status.disabled && (
          <input
            ref={inputRef}
            value={inputValue}
            id={id}
            className="inline-block w-full min-w-40 flex-1 py-1 pl-[2px] text-body-m"
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => {
              setStatus(prev => ({ ...prev, focused: false }));
              addValue();
            }}
            onFocus={() => {
              setStatus(prev => ({ ...prev, focused: true }));
            }}
            autoFocus={autoFocus}
            data-autofocus={autoFocus ? 'true' : undefined}
          />
        )}
      </VFlex>
    );
  },
);

export default VInnerChipInput;
